/**
 * Tabular (Table) Layout
 *
 * Displays items in a table/grid format with sortable columns.
 */

import { useMemo, useCallback, useState, useRef, useEffect } from 'react';
import type React from 'react';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Checkbox } from '@/components/ui/checkbox';
import { Button } from '@/components/ui/button';
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuTrigger,
	DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { FieldList } from '@/components/content/FieldList';
import { Badge } from '@/components/ui/badge';
import {
	ArrowUp,
	ArrowDown,
	ArrowUpDown,
	MoreHorizontal,
	Loader2,
	AlignLeft,
	AlignCenter,
	AlignRight,
	EyeOff,
	Plus,
	Image as ImageIcon,
	File as FileIcon,
	GripVertical,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import type { LayoutProps, TabularLayoutOptions, LayoutItem } from '../types';

export function TabularLayout({
	collection: _collection,
	primaryKeyField = 'id',
	items,
	loading = false,
	error,
	page: _page,
	limit: _limit,
	totalCount: _totalCount,
	itemCount: _itemCount,
	totalPages: _totalPages = 1,
	selection,
	onSelectionChange,
	showSelect = 'multiple',
	sort = [],
	onSortChange,
	layoutOptions,
	onLayoutOptionsChange,
	layoutQuery,
	onLayoutQueryChange,
	fields,
	onItemClick,
	readonly = false,
}: LayoutProps<TabularLayoutOptions>) {
	const [addFieldOpen, setAddFieldOpen] = useState(false);
	const [draggingField, setDraggingField] = useState<string | null>(null);
	const [dragOverField, setDragOverField] = useState<string | null>(null);
	// true  → insert AFTER the target  (pointer on right half, i.e. left→right drag)
	// false → insert BEFORE the target (pointer on left  half, i.e. right→left drag)
	const [dragInsertAfter, setDragInsertAfter] = useState(false);

	// Column resize state — live widths during drag, committed to layoutOptions on release
	const [localWidths, setLocalWidths] = useState<Record<string, number>>(() => layoutOptions.widths || {});
	const resizeState = useRef<{ field: string; startX: number; startWidth: number } | null>(null);

	// Column-reorder drag — we use native document listeners (not React synthetic
	// events) because React's delegation layer does not reliably re-dispatch
	// pointer-capture events that the browser routes directly to a DOM node.
	const headRowRef = useRef<HTMLTableRowElement>(null);
	// Always-fresh refs so the document listeners (created once per pointerdown)
	// never close over stale values.
	const layoutOptionsRef = useRef(layoutOptions);
	layoutOptionsRef.current = layoutOptions;

	// Sync local widths when preset widths change from outside (e.g. switching collection)
	useEffect(() => {
		if (!resizeState.current) {
			setLocalWidths(layoutOptions.widths || {});
		}
	}, [layoutOptions.widths]);

	// Start column resize on pointer down on the resize handle
	const handleResizeStart = useCallback(
		(e: React.PointerEvent<HTMLDivElement>, fieldName: string) => {
			e.preventDefault();
			e.stopPropagation();
			(e.target as HTMLElement).setPointerCapture(e.pointerId);
			const th = (e.target as HTMLElement).parentElement as HTMLElement;
			const startWidth = localWidths[fieldName] ?? th.offsetWidth;
			resizeState.current = { field: fieldName, startX: e.clientX, startWidth };
		},
		[localWidths]
	);

	const handleResizeMove = useCallback((e: React.PointerEvent<HTMLDivElement>, fieldName: string) => {
		if (!resizeState.current || resizeState.current.field !== fieldName) return;
		const dx = e.clientX - resizeState.current.startX;
		const newWidth = Math.max(50, resizeState.current.startWidth + dx);
		setLocalWidths((prev) => ({ ...prev, [fieldName]: newWidth }));
	}, []);

	const handleResizeEnd = useCallback(
		(e: React.PointerEvent<HTMLDivElement>, fieldName: string) => {
			if (!resizeState.current || resizeState.current.field !== fieldName) return;
			const dx = e.clientX - resizeState.current.startX;
			const newWidth = Math.max(50, resizeState.current.startWidth + dx);
			resizeState.current = null;
			setLocalWidths((prev) => {
				const newWidths = { ...prev, [fieldName]: newWidth };
				onLayoutOptionsChange({ ...layoutOptions, widths: newWidths });
				return newWidths;
			});
		},
		[layoutOptions, onLayoutOptionsChange]
	);

	const fieldMap = useMemo(() => new Map(fields.map((f) => [f.field, f])), [fields]);

	// Determine current field order (supports dot-notation columns)
	const currentFieldOrder = useMemo(() => {
		const configured = layoutQuery.fields || layoutOptions.fields;
		if (configured && configured.length > 0) return configured;

		return fields
			.filter((f) => !f.field.startsWith('$') && f.field !== primaryKeyField)
			.slice(0, 5)
			.map((f) => f.field);
	}, [fields, layoutOptions.fields, layoutQuery.fields, primaryKeyField]);

	// Always-fresh ref so pointer handlers never read a stale field order.
	const currentFieldOrderRef = useRef(currentFieldOrder);
	currentFieldOrderRef.current = currentFieldOrder;
	const layoutQueryRef2 = useRef(layoutQuery);
	layoutQueryRef2.current = layoutQuery;


	// ── Column-reorder: single pointerdown handler + native document listeners ──
	// React's synthetic event system does not reliably re-dispatch pointer-capture
	// events (pointermove / pointerup after setPointerCapture).  Attaching native
	// listeners directly to `document` inside the pointerdown handler is the
	// standard, cross-browser solution for drag operations.
	const onGripPointerDown = useCallback(
		(e: React.PointerEvent<HTMLElement>, fieldName: string) => {
			e.preventDefault();
			e.stopPropagation();

			// Local closure state — no stale-closure risk, no extra refs needed.
			const state = { live: false, overField: null as string | null, insertAfter: false };

			// Make the cursor a grabbing hand across the whole page and suppress
			// accidental text selection for the lifetime of this drag gesture.
			document.body.style.cursor = 'grabbing';
			document.body.style.userSelect = 'none';

			const onMove = (mv: PointerEvent) => {
				if (!state.live) {
					state.live = true;
					setDraggingField(fieldName);
				}

				// Find which column header the pointer is over, and which HALF
				// of that column it is on.  Left half → insert before the target
				// (right→left drag); right half → insert after (left→right drag).
				const row = headRowRef.current;
				if (!row) return;
				const cells = row.querySelectorAll<HTMLElement>('th[data-col]');
				let found: string | null = null;
				let after = false;
				for (const cell of cells) {
					const { left, right } = cell.getBoundingClientRect();
					if (mv.clientX >= left && mv.clientX <= right) {
						const name = cell.getAttribute('data-col');
						if (name && name !== fieldName) {
							found = name;
							after = mv.clientX > (left + right) / 2;
						}
						break;
					}
				}
				if (found !== state.overField || after !== state.insertAfter) {
					state.overField = found;
					state.insertAfter = after;
					setDragOverField(found);
					setDragInsertAfter(after);
				}
			};

			const onUp = () => {
				document.removeEventListener('pointermove', onMove);
				document.removeEventListener('pointerup', onUp);
				document.removeEventListener('pointercancel', onUp);

				// Restore cursor and text-selection behaviour.
				document.body.style.cursor = '';
				document.body.style.userSelect = '';

				if (state.live && state.overField) {
					// Commit reorder — read always-fresh refs, never stale closures.
					const order = [...currentFieldOrderRef.current];
					const fromIdx = order.indexOf(fieldName);
					let toIdx = order.indexOf(state.overField);
					if (fromIdx !== -1 && toIdx !== -1) {
						order.splice(fromIdx, 1);
						// Adjust toIdx for the removal, then shift +1 when dropping
						// on the right half (insert after the target).
						if (fromIdx < toIdx) toIdx -= 1;
						if (state.insertAfter) toIdx += 1;
						order.splice(Math.min(toIdx, order.length), 0, fieldName);
						onLayoutQueryChange({ ...layoutQueryRef2.current, fields: order });
						const opts = layoutOptionsRef.current;
						if (opts.fields) {
							const { fields: _omit, ...rest } = opts;
							onLayoutOptionsChange(rest);
						}
					}
				}

				setDraggingField(null);
				setDragOverField(null);
				setDragInsertAfter(false);
			};

			document.addEventListener('pointermove', onMove);
			document.addEventListener('pointerup', onUp);
			document.addEventListener('pointercancel', onUp);
		},
		[onLayoutQueryChange, onLayoutOptionsChange]
	);

	const previewFieldOrder = useMemo(() => {
		if (!draggingField || !dragOverField || draggingField === dragOverField) {
			return currentFieldOrder;
		}

		const order = [...currentFieldOrder];
		const fromIndex = order.indexOf(draggingField);
		let toIndex = order.indexOf(dragOverField);
		if (fromIndex === -1 || toIndex === -1) return currentFieldOrder;

		order.splice(fromIndex, 1);
		if (fromIndex < toIndex) toIndex -= 1;
		if (dragInsertAfter) toIndex += 1;
		order.splice(Math.min(toIndex, order.length), 0, draggingField);
		return order;
	}, [currentFieldOrder, draggingField, dragOverField, dragInsertAfter]);

	type Column = {
		name: string;
		parts: string[];
		baseField: (typeof fields)[number];
		label: string;
	};

	const columns: Column[] = useMemo(() => {
		const toTitle = (value: string) =>
			value
				.split('_')
				.map((part) => part.charAt(0).toUpperCase() + part.slice(1))
				.join(' ');
		return previewFieldOrder
			.map((name) => {
				const parts = name.split('.');
				const baseField = fieldMap.get(parts[0]!);
				if (!baseField) return null;
				// Use field.name which comes from translations or formatted field name
				const baseLabel = baseField.name || toTitle(baseField.field);
				const label = parts.length > 1 ? `${baseLabel} > ${parts.slice(1).map(toTitle).join(' > ')}` : baseLabel;
				return { name, parts, baseField, label };
			})
			.filter((c): c is Column => Boolean(c));
	}, [previewFieldOrder, fieldMap]);

	// Default column width when no explicit width is saved
	const DEFAULT_COL_WIDTH = 220;
	// Checkbox + action columns combined rough width
	const FIXED_COLS_WIDTH = (showSelect !== 'none' ? 48 : 0) + (!readonly ? 48 : 0);

	// Total table width = sum of all data-column widths + fixed columns.
	// Used with table-layout:fixed so resizing one column doesn't push others.
	const totalTableWidth = useMemo(() => {
		const dataWidth = columns.reduce((sum, col) => sum + (localWidths[col.name] ?? DEFAULT_COL_WIDTH), 0);
		return dataWidth + FIXED_COLS_WIDTH;
	}, [columns, localWidths, FIXED_COLS_WIDTH]);

	// Get spacing class based on options
	const spacingClass = useMemo(() => {
		switch (layoutOptions.spacing) {
			case 'compact':
				return 'py-1';
			case 'comfortable':
				return 'py-4';
			default:
				return 'py-2'; // cozy
		}
	}, [layoutOptions.spacing]);

	// Check if all items are selected
	const allSelected = useMemo(() => {
		if (items.length === 0) return false;
		return items.every((item) => selection.includes(item[primaryKeyField] as string | number));
	}, [items, selection, primaryKeyField]);

	// Toggle all selection
	const handleSelectAll = useCallback(() => {
		if (allSelected) {
			onSelectionChange([]);
		} else {
			onSelectionChange(items.map((item) => item[primaryKeyField] as string | number));
		}
	}, [allSelected, items, primaryKeyField, onSelectionChange]);

	// Toggle single item selection
	const handleToggleSelection = useCallback(
		(itemKey: string | number) => {
			if (selection.includes(itemKey)) {
				onSelectionChange(selection.filter((k) => k !== itemKey));
			} else {
				onSelectionChange([...selection, itemKey]);
			}
		},
		[selection, onSelectionChange]
	);

	// Handle sort change
	const handleSort = useCallback(
		(field: string) => {
			if (!onSortChange) return;

			const currentSort = sort[0];
			let newSort: string[];

			if (currentSort === field) {
				// Ascending -> Descending
				newSort = [`-${field}`];
			} else if (currentSort === `-${field}`) {
				// Descending -> No sort
				newSort = [];
			} else {
				// No sort -> Ascending
				newSort = [field];
			}

			onSortChange(newSort);
			onLayoutQueryChange({ ...layoutQuery, sort: newSort });
		},
		[sort, onSortChange, layoutQuery, onLayoutQueryChange]
	);

	// Get sort direction for a field
	const getSortDirection = useCallback(
		(field: string) => {
			if (sort.includes(field)) return 'asc';
			if (sort.includes(`-${field}`)) return 'desc';
			return null;
		},
		[sort]
	);

	// Apply template strings like "{{first_name}} {{last_name}}"
	const applyTemplate = useCallback((template: string, record: Record<string, unknown>) => {
		return template
			.replace(/\{\{([^}]+)\}\}/g, (_match, key) => {
				const trimmed = String(key).trim();
				// Support dot notation
				const value = trimmed.split('.').reduce<unknown>((acc, part) => {
					if (acc && typeof acc === 'object' && part in acc) {
						return (acc as Record<string, unknown>)[part];
					}
					return undefined;
				}, record);
				if (value === null || value === undefined) return '';
				return String(value);
			})
			.trim();
	}, []);

	// Format cell value for display (respect template)
	// Returns either a string or React element (for badges)
	const formatCellValue = useCallback(
		(value: unknown, field: (typeof fields)[0], fieldPath: string): string | React.ReactNode => {
			if (value === null || value === undefined) return '—';

			const type = field.type?.toLowerCase() || field.schema?.data_type?.toLowerCase();
			const specials = field.meta?.special || [];
			const interfaceType = field.meta?.interface;

			// Handle file/image interface fields - show icon instead of trying to load image
			if (interfaceType === 'file' || interfaceType === 'file-image') {
				if (typeof value === 'object' && value !== null) {
					const fileData = value as { filename_download?: string; type?: string };
					const isImage = interfaceType === 'file-image' || fileData.type?.startsWith('image/');
					return (
						<div className="flex items-center gap-2">
							{isImage ? (
								<ImageIcon className="h-4 w-4 text-blue-600" />
							) : (
								<FileIcon className="h-4 w-4 text-muted-foreground" />
							)}
							<span className="text-sm truncate">{fileData.filename_download || 'File'}</span>
						</div>
					);
				} else if (typeof value === 'string') {
					// Just the file ID
					const isImage = interfaceType === 'file-image';
					return (
						<div className="flex items-center gap-2">
							{isImage ? (
								<ImageIcon className="h-4 w-4 text-blue-600" />
							) : (
								<FileIcon className="h-4 w-4 text-muted-foreground" />
							)}
							<span className="text-sm text-muted-foreground">File</span>
						</div>
					);
				}
			}

			// Check if this is a multi-select dropdown field
			const isMultiSelect = interfaceType === 'select-multiple-dropdown';

			// Handle multi-select dropdown - display as badges with choice labels
			if (isMultiSelect) {
				// Handle both array and CSV string formats
				let valuesArray: (string | number)[] = [];

				if (Array.isArray(value)) {
					valuesArray = value.filter((v) => v !== null && v !== undefined) as (string | number)[];
				} else if (typeof value === 'string' && value.trim() !== '') {
					// Parse CSV string (comma-separated values)
					valuesArray = value
						.split(',')
						.map((v) => v.trim())
						.filter((v) => v.length > 0);
				}

				if (valuesArray.length === 0) return '—';

				// Get choices from field options
				const choices = (field.meta?.options?.choices as Array<{ text: string; value: string }>) || [];

				// Helper to get display text for a value
				const getDisplayText = (val: string | number): string => {
					const choice = choices.find((c) => String(c.value) === String(val));
					return choice?.text || String(val);
				};

				const badges = valuesArray
					.slice(0, 5) // Show up to 5 badges
					.map((item, idx) => (
						<Badge key={idx} variant="secondary" className="mr-1">
							{getDisplayText(item)}
						</Badge>
					));

				if (badges.length > 0) {
					const moreCount = valuesArray.length - badges.length;
					return (
						<div className="flex flex-wrap gap-1 items-center">
							{badges}
							{moreCount > 0 && <span className="text-xs text-muted-foreground">+{moreCount}</span>}
						</div>
					);
				}
			}

			// Check if this is a M2M field (array of objects/IDs)
			// Also check if value is an array with nested path (e.g., "category.category_id.name")
			const isM2M = specials.includes('m2m') || specials.includes('o2m');
			const hasNestedPath = fieldPath.split('.').length > 1;
			const isArrayValue = Array.isArray(value);

			// Helper: extract a human-readable display string from a junction/related item object.
			// Tries display template first, then common name-like fields, then ID.
			const extractObjectDisplay = (item: unknown, displayTemplate?: string): string => {
				if (item === null || item === undefined) return '';
				if (typeof item !== 'object') return String(item);
				const obj = item as Record<string, unknown>;

				// Apply display template if provided (e.g. "{{name}}" or "{{first_name}} {{last_name}}")
				if (displayTemplate) {
					const rendered = displayTemplate
						.replace(/\{\{([\w.]+)\}\}/g, (_, key: string) => {
							const parts = key.trim().split('.');
							let val: unknown = obj;
							for (const p of parts) {
								if (val && typeof val === 'object') val = (val as Record<string, unknown>)[p];
								else {
									val = undefined;
									break;
								}
							}
							return val != null ? String(val) : '';
						})
						.trim();
					if (rendered) return rendered;
				}

				// Try common display field names in priority order
				for (const key of ['name', 'title', 'label', 'display_name', 'full_name', 'email', 'username', 'code']) {
					const v = obj[key];
					if (v != null && typeof v === 'string' && v.trim()) return v.trim();
					if (v != null && typeof v === 'number') return String(v);
				}

				// Try any non-ID string field
				for (const [key, val] of Object.entries(obj)) {
					if (key === 'id' || key.endsWith('_id') || key.endsWith('_at') || key === 'sort') continue;
					if (typeof val === 'string' && val.trim()) return val.trim();
				}

				// Fallback to ID
				return obj['id'] != null ? String(obj['id']) : '';
			};

			// Handle M2M/O2M arrays - display as badges
			// Also handle arrays that come from nested paths (M2M relationships)
			if ((isM2M || (isArrayValue && hasNestedPath)) && isArrayValue) {
				if (value.length === 0) return '—';

				const pathParts = fieldPath.split('.');
				const fieldDisplayTemplate = field.meta?.options?.template as string | undefined;

				// Inline helper: render a list of display strings as badges with a
				// popover that shows ALL items when there are more than VISIBLE_MAX.
				const VISIBLE_MAX = 5;
				const renderBadgesWithOverflow = (displayStrings: string[]): React.ReactNode => {
					if (displayStrings.length === 0) return null;
					const visible = displayStrings.slice(0, VISIBLE_MAX);
					const overflow = displayStrings.slice(VISIBLE_MAX);
					return (
						<div className="flex flex-wrap gap-1 items-center">
							{visible.map((s, i) => (
								<Badge key={i} variant="secondary">
									{s}
								</Badge>
							))}
							{overflow.length > 0 && (
								<Popover>
									<PopoverTrigger asChild>
										<button
											type="button"
											className="inline-flex items-center rounded-full border border-primary/40 bg-primary/10 px-2 py-0.5 text-xs font-medium text-primary transition-colors hover:bg-primary/20 cursor-pointer"
											onClick={(e) => e.stopPropagation()}
										>
											+{overflow.length} more
										</button>
									</PopoverTrigger>
									<PopoverContent
										className="w-72 p-0 overflow-hidden shadow-lg"
										align="start"
										onClick={(e) => e.stopPropagation()}
									>
										<div className="border-b bg-muted/60 px-3 py-2">
											<p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
												All items ({displayStrings.length})
											</p>
										</div>
										<div className="flex flex-wrap gap-1.5 p-3 max-h-52 overflow-y-auto">
											{displayStrings.map((s, i) => (
												<Badge key={i} className="border-0 bg-primary/10 text-primary hover:bg-primary/20">
													{s}
												</Badge>
											))}
										</div>
									</PopoverContent>
								</Popover>
							)}
						</div>
					);
				};

				// Collect display strings for each item
				const displayStrings: string[] = [];

				if (pathParts.length > 2) {
					// Path like "category.category_id.name"
					const junctionField = pathParts[1];
					const nestedField = pathParts[2];
					if (!junctionField || !nestedField) return '—';

					for (const item of value) {
						if (item === null || item === undefined) continue;
						if (typeof item === 'object') {
							let itemValue: string | null = null;
							if (junctionField && item[junctionField]) {
								const junctionValue = item[junctionField];
								if (typeof junctionValue === 'object' && junctionValue !== null) {
									const remainingParts = pathParts.slice(2);
									let deepVal: unknown = junctionValue;
									for (const p of remainingParts) {
										if (deepVal && typeof deepVal === 'object') deepVal = (deepVal as Record<string, unknown>)[p];
										else {
											deepVal = undefined;
											break;
										}
									}
									itemValue =
										deepVal != null ? String(deepVal) : extractObjectDisplay(junctionValue, fieldDisplayTemplate);
								} else {
									itemValue = String(junctionValue);
								}
							} else if (nestedField && item[nestedField] !== undefined) {
								itemValue = String(item[nestedField]);
							} else {
								itemValue = extractObjectDisplay(item, fieldDisplayTemplate);
							}
							if (itemValue) displayStrings.push(itemValue);
						} else {
							displayStrings.push(String(item));
						}
					}
				} else if (pathParts.length === 2) {
					// Path like "category.category_id"
					const junctionField = pathParts[1];
					for (const item of value) {
						if (item === null || item === undefined) continue;
						let displayStr = '';
						if (typeof item === 'object' && junctionField && item[junctionField] !== undefined) {
							const junctionValue = item[junctionField];
							displayStr =
								typeof junctionValue === 'object' && junctionValue !== null
									? extractObjectDisplay(junctionValue, fieldDisplayTemplate)
									: String(junctionValue);
						} else if (typeof item === 'object') {
							displayStr = extractObjectDisplay(item, fieldDisplayTemplate);
						} else {
							displayStr = String(item);
						}
						if (displayStr) displayStrings.push(displayStr);
					}
				} else {
					// Simple M2M: each item is a junction row object
					for (const item of value) {
						if (item === null || item === undefined) continue;
						const displayStr =
							typeof item === 'object' ? extractObjectDisplay(item, fieldDisplayTemplate) : String(item);
						if (displayStr) displayStrings.push(displayStr);
					}
				}

				const result = renderBadgesWithOverflow(displayStrings);
				if (result) return result;
			}

			// Template-based rendering when value is an object and template exists
			const template = field.meta?.options?.template as string | undefined;
			if (template && typeof value === 'object' && value !== null && !Array.isArray(value)) {
				const rendered = applyTemplate(template, value as Record<string, unknown>);
				if (rendered) return rendered;
			}

			// Render user template-style values
			if (typeof value === 'object' && value !== null && !Array.isArray(value)) {
				const maybeUser = value as { first_name?: string | null; last_name?: string | null; email?: string | null };
				if ('first_name' in maybeUser || 'last_name' in maybeUser || 'email' in maybeUser) {
					const full = `${maybeUser.first_name || ''} ${maybeUser.last_name || ''}`.trim();
					if (full) return full;
					if (maybeUser.email) return maybeUser.email;
				}
			}

			if (type === 'boolean') return value ? 'Yes' : 'No';

			if (type === 'json' || (typeof value === 'object' && !Array.isArray(value))) {
				const json = JSON.stringify(value);
				return json.length > 50 ? json.substring(0, 50) + '...' : json;
			}

			if (type === 'datetime' || type === 'timestamp' || type === 'date') {
				try {
					return new Date(value as string).toLocaleString();
				} catch {
					return String(value);
				}
			}

			const strValue = String(value);
			return strValue.length > 50 ? strValue.substring(0, 50) + '...' : strValue;
		},
		[applyTemplate]
	);

	// Handle row click
	const handleRowClick = useCallback(
		(item: LayoutItem) => {
			if (readonly) return;
			if (onItemClick) {
				onItemClick(item);
			}
		},
		[onItemClick, readonly]
	);

	// Handle alignment change
	const handleAlignChange = useCallback(
		(fieldName: string, align: 'left' | 'center' | 'right') => {
			if (!onLayoutOptionsChange) return;
			const newAlign = { ...layoutOptions.align, [fieldName]: align };
			onLayoutOptionsChange({ ...layoutOptions, align: newAlign });
		},
		[layoutOptions, onLayoutOptionsChange]
	);

	// Handle hide field
	const handleHideField = useCallback(
		(fieldName: string) => {
			// Get current fields or initialize with visible fields
			let currentFields = layoutQuery.fields || layoutOptions.fields || currentFieldOrder;
			if (!currentFields || currentFields.length === 0) {
				currentFields = currentFieldOrder;
			}
			const newFields = currentFields.filter((f) => f !== fieldName);
			onLayoutQueryChange({ ...layoutQuery, fields: newFields });
			if (layoutOptions.fields) {
				const { fields: _omit, ...rest } = layoutOptions;
				onLayoutOptionsChange(rest);
			}
		},
		[layoutOptions, layoutQuery, currentFieldOrder, onLayoutOptionsChange, onLayoutQueryChange]
	);

	// Handle add field
	const handleAddField = useCallback(
		(fieldName: string) => {
			// Get current fields or initialize with visible fields
			let currentFields = layoutQuery.fields || layoutOptions.fields || currentFieldOrder;
			if (!currentFields || currentFields.length === 0) {
				currentFields = currentFieldOrder;
			}
			if (!currentFields.includes(fieldName)) {
				const newFields = [...currentFields, fieldName];
				onLayoutQueryChange({ ...layoutQuery, fields: newFields });
				if (layoutOptions.fields) {
					const { fields: _omit, ...rest } = layoutOptions;
					onLayoutOptionsChange(rest);
				}
			}
			setAddFieldOpen(false);
		},
		[layoutOptions, layoutQuery, currentFieldOrder, onLayoutOptionsChange, onLayoutQueryChange]
	);

	if (loading && items.length === 0) {
		return (
			<div className="flex items-center justify-center py-12">
				<Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
			</div>
		);
	}

	if (error) {
		return (
			<div className="flex items-center justify-center py-12">
				<p className="text-destructive">Error loading items: {error.message}</p>
			</div>
		);
	}

	if (items.length === 0) {
		return (
			<div className="flex flex-col items-center justify-center py-12">
				<p className="text-muted-foreground">No items found</p>
			</div>
		);
	}

	const scrollContainerRef = useRef<HTMLDivElement>(null);

	return (
		<div className="w-full h-full flex flex-col">
			{/* Main scroll container */}
			<div ref={scrollContainerRef} className="flex-1 overflow-auto tabular-scroll-container">
				<Table style={{ tableLayout: 'fixed', width: totalTableWidth }}>
					<TableHeader className="sticky top-0 z-10 bg-background shadow-[0_1px_0_0] shadow-border">
						<TableRow ref={headRowRef}>
							{showSelect !== 'none' && (
								<TableHead className="w-12">
									{showSelect === 'multiple' && <Checkbox checked={allSelected} onCheckedChange={handleSelectAll} />}
								</TableHead>
							)}
							{columns.map((column) => (
								<TableHead
									key={column.name}
									data-col={column.name}
									className={cn(
										'group select-none relative !h-auto !whitespace-normal py-2 transition-colors duration-100',
										draggingField === column.name
											? 'opacity-40 bg-muted'
											: dragOverField === column.name
												? 'bg-primary/10'
												: '',
										layoutOptions.align?.[column.name] === 'center' && 'text-center',
										layoutOptions.align?.[column.name] === 'right' && 'text-right'
									)}
									style={{ width: localWidths[column.name] ?? DEFAULT_COL_WIDTH, minWidth: 0 }}
								>
									{/* Drop-target indicator: left edge = insert before, right edge = insert after */}
									{dragOverField === column.name && draggingField !== column.name && (
										<div
											className={cn(
												'pointer-events-none absolute top-0 z-20 h-full w-0.5 rounded-full bg-primary',
												dragInsertAfter ? 'right-0' : 'left-0'
											)}
										/>
									)}
									<div className="flex items-center gap-0.5">
										{/* Drag handle — pointer events here initiate the column reorder.
										    Using pointer capture means the move/up events are delivered
										    globally even if the pointer leaves this element mid-drag. */}
										<div
											className="cursor-grab active:cursor-grabbing touch-none shrink-0"
											onPointerDown={(e) => onGripPointerDown(e, column.name)}
										>
											<GripVertical className="h-4 w-4 text-muted-foreground/30 transition-colors group-hover:text-muted-foreground/70" />
										</div>
										<DropdownMenu>
											<DropdownMenuTrigger asChild>
												<Button
													variant="ghost"
													size="sm"
													className="-ml-1 h-auto py-1 flex-1 justify-start font-medium hover:bg-transparent min-w-0 whitespace-normal text-left"
													onClick={(e) => e.stopPropagation()}
												>
													<span className="flex-1 text-left break-words min-w-0">{column.label}</span>
													{getSortDirection(column.name) === 'asc' && <ArrowUp className="h-3.5 w-3.5 ml-1 shrink-0" />}
													{getSortDirection(column.name) === 'desc' && (
														<ArrowDown className="h-3.5 w-3.5 ml-1 shrink-0" />
													)}
													{!getSortDirection(column.name) && (
														<ArrowUpDown className="h-3.5 w-3.5 ml-1 shrink-0 opacity-0 group-hover:opacity-50" />
													)}
												</Button>
											</DropdownMenuTrigger>
											<DropdownMenuContent align="start">
												<DropdownMenuItem
													onClick={() => handleSort(column.name)}
													className={cn(getSortDirection(column.name) === 'asc' && 'bg-accent')}
												>
													<ArrowUp className="h-4 w-4 mr-2" />
													Sort Ascending
												</DropdownMenuItem>
												<DropdownMenuItem
													onClick={() => handleSort(column.name)}
													className={cn(getSortDirection(column.name) === 'desc' && 'bg-accent')}
												>
													<ArrowDown className="h-4 w-4 mr-2" />
													Sort Descending
												</DropdownMenuItem>
												<DropdownMenuSeparator />
												<DropdownMenuItem
													onClick={() => handleAlignChange(column.name, 'left')}
													className={cn(layoutOptions.align?.[column.name] === 'left' && 'bg-accent')}
												>
													<AlignLeft className="h-4 w-4 mr-2" />
													Left Align
												</DropdownMenuItem>
												<DropdownMenuItem
													onClick={() => handleAlignChange(column.name, 'center')}
													className={cn(layoutOptions.align?.[column.name] === 'center' && 'bg-accent')}
												>
													<AlignCenter className="h-4 w-4 mr-2" />
													Center Align
												</DropdownMenuItem>
												<DropdownMenuItem
													onClick={() => handleAlignChange(column.name, 'right')}
													className={cn(layoutOptions.align?.[column.name] === 'right' && 'bg-accent')}
												>
													<AlignRight className="h-4 w-4 mr-2" />
													Right Align
												</DropdownMenuItem>
												<DropdownMenuSeparator />
												<DropdownMenuItem onClick={() => handleHideField(column.name)}>
													<EyeOff className="h-4 w-4 mr-2" />
													Hide Field
												</DropdownMenuItem>
											</DropdownMenuContent>
										</DropdownMenu>
									</div>
									{/* Resize handle */}
									<div
										className="absolute right-0 top-0 h-full w-1.5 cursor-col-resize hover:bg-primary/40 active:bg-primary/60 z-10"
										onPointerDown={(e) => handleResizeStart(e, column.name)}
										onPointerMove={(e) => handleResizeMove(e, column.name)}
										onPointerUp={(e) => handleResizeEnd(e, column.name)}
										onPointerCancel={(e) => handleResizeEnd(e, column.name)}
										onClick={(e) => e.stopPropagation()}
										draggable={false}
										onDragStart={(e) => e.preventDefault()}
									/>
								</TableHead>
							))}
							{/* Add field button */}
							<TableHead className="w-12">
								<DropdownMenu open={addFieldOpen} onOpenChange={setAddFieldOpen}>
									<DropdownMenuTrigger asChild>
										<Button variant="ghost" size="icon" className="h-8 w-8" title="Add Field">
											<Plus className="h-4 w-4" />
										</Button>
									</DropdownMenuTrigger>
									<DropdownMenuContent align="end" className="w-64 p-0" onCloseAutoFocus={(e) => e.preventDefault()}>
										<FieldList
											collection={_collection}
											disabledFields={currentFieldOrder}
											onAdd={handleAddField}
											relationalFieldSelectable={true}
										/>
									</DropdownMenuContent>
								</DropdownMenu>
							</TableHead>
							{!readonly && <TableHead className="w-12" />}
						</TableRow>
					</TableHeader>
					<TableBody>
						{items.map((item) => {
							const itemKey = item[primaryKeyField] as string | number;
							const isSelected = selection.includes(itemKey);

							return (
								<TableRow
									key={itemKey}
									className={cn('cursor-pointer hover:bg-muted/50', isSelected && 'bg-muted/30')}
									onClick={() => handleRowClick(item)}
								>
									{showSelect !== 'none' && (
										<TableCell className={spacingClass} onClick={(e) => e.stopPropagation()}>
											<Checkbox checked={isSelected} onCheckedChange={() => handleToggleSelection(itemKey)} />
										</TableCell>
									)}
									{columns.map((column) => {
										// Extract cell value, handling arrays for M2M fields
										let cellValue: unknown = item;
										for (let i = 0; i < column.parts.length; i++) {
											const part = column.parts[i]!;

											// If current value is an array, we're dealing with M2M
											// Return the array and let formatCellValue handle nested extraction
											if (Array.isArray(cellValue)) {
												break;
											}

											// Normal object traversal
											if (
												cellValue &&
												typeof cellValue === 'object' &&
												part in (cellValue as Record<string, unknown>)
											) {
												cellValue = (cellValue as Record<string, unknown>)[part];
											} else {
												cellValue = undefined;
												break;
											}
										}

										const formatted = formatCellValue(cellValue, column.baseField, column.name);

										return (
											<TableCell
												key={column.name}
												className={cn(
													spacingClass,
													// Keep colour transitions smooth as columns reorder live.
													'transition-colors duration-100',
													layoutOptions.align?.[column.name] === 'center' && 'text-center',
													layoutOptions.align?.[column.name] === 'right' && 'text-right',
													// Mirror the header's drag feedback so the whole column
													// appears to ghost / highlight — not just the header cell.
													draggingField === column.name && 'opacity-40 bg-muted/50',
													dragOverField === column.name &&
														draggingField !== column.name &&
														'bg-primary/[0.04]',
												)}
												style={{
													width: localWidths[column.name] ?? DEFAULT_COL_WIDTH,
													minWidth: 0,
													overflow: 'hidden',
												}}
											>
												{formatted}
											</TableCell>
										);
									})}
									{!readonly && (
										<TableCell className={spacingClass} onClick={(e) => e.stopPropagation()}>
											<DropdownMenu>
												<DropdownMenuTrigger asChild>
													<Button variant="ghost" size="icon" className="h-8 w-8">
														<MoreHorizontal className="h-4 w-4" />
													</Button>
												</DropdownMenuTrigger>
												<DropdownMenuContent align="end">
													<DropdownMenuItem onClick={() => handleRowClick(item)}>Edit</DropdownMenuItem>
												</DropdownMenuContent>
											</DropdownMenu>
										</TableCell>
									)}
								</TableRow>
							);
						})}
					</TableBody>
				</Table>
			</div>
		</div>
	);
}
